// AUPRÉA — Dashboards Étape 8 (Admin + Consultant + Dossiers)
import { useEffect, useState, useMemo } from "react";
import { C, fmtEur, Badge, Btn, Input, Select, Spinner, Alert, leadsV2Api, fdate } from "./shared";
import api, { dashboardApi } from "./api";
import { MesObjectifs } from "./Objectifs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

// ── Statuts pastille ─────────────────────────────────────────────────────────
export const STATUT_META = {
  rdv_planifie:        { label: "RDV planifié",         color: "#185FA5", bg: "#E6F1FB" },
  bilan_realise:       { label: "Bilan réalisé",        color: "#5F5E5A", bg: "#F1EFEC" },
  proposition_envoyee: { label: "Proposition envoyée",  color: "#C8902C", bg: "#F7EBCF" },
  virement_attente:    { label: "Virement en attente",  color: "#B45309", bg: "#FEF3C7" },
  paye:                { label: "Payé",                 color: "#1F7A6C", bg: "#DBEEE9" },
  mission_en_cours:    { label: "Mission en cours",     color: "#1B2A4A", bg: "#E3E8F2" },
  mission_terminee:    { label: "Mission terminée",     color: "#2E7D4F", bg: "#DCF2E3" },
  expiree:             { label: "Expirée / Sans suite", color: "#991B1B", bg: "#FEE2E2" },
};

export const StatutBadge = ({ statut }) => {
  const m = STATUT_META[statut] || { label: statut, color: C.mid, bg: C.gray2 };
  return (
    <span data-testid={`statut-badge-${statut}`} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color }} />
      {m.label}
    </span>
  );
};

// ── Sélecteur de mois ────────────────────────────────────────────────────────
const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

const shiftMonth = (yyyymm, delta) => {
  const [y, m] = yyyymm.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const MonthPicker = ({ value, onChange }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "4px 6px" }}>
    <button data-testid="month-prev" onClick={() => onChange(shiftMonth(value, -1))} style={btnIcon}>‹</button>
    <div style={{ minWidth: 150, textAlign: "center", fontSize: 13, fontWeight: 600, color: C.navy, textTransform: "capitalize" }} data-testid="month-label">
      {monthLabel(value)}
    </div>
    <button data-testid="month-next" onClick={() => onChange(shiftMonth(value, +1))} style={btnIcon}>›</button>
    <button data-testid="month-today" onClick={() => onChange(currentMonth())} style={{ ...btnIcon, fontSize: 11, padding: "0 8px", color: C.gold }}>Aujourd'hui</button>
  </div>
);

const btnIcon = {
  width: 26, height: 26, borderRadius: 6, border: "none", background: "transparent",
  cursor: "pointer", fontSize: 16, color: C.mid, fontFamily: "inherit",
};

// ── KPI cards ────────────────────────────────────────────────────────────────
const Kpi = ({ label, value, sub, color, ...rest }) => (
  <div {...rest} style={{
    background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`,
    padding: 16, position: "relative", overflow: "hidden",
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />
    <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color, marginBottom: sub ? 6 : 0 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.mid }}>{sub}</div>}
  </div>
);

const Section = ({ title, right, children, ...rest }) => (
  <div {...rest} style={{ marginBottom: 22 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".09em" }}>{title}</div>
      <div style={{ flex: 1, height: .5, background: C.border }} />
      {right}
    </div>
    {children}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 16, ...(style || {}) }}>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// CONSULTANT DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════
export const ConseillerDashboardV2 = ({ onNavigate, onDemarrerBilan }) => {
  const [mois, setMois] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyOk, setCopyOk] = useState(false);

  const [aRelancer, setARelancer] = useState(0);
  useEffect(() => { leadsV2Api.list().then(l => setARelancer(l.filter(x => x.a_relancer).length)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    dashboardApi.consultant(mois).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [mois]);

  if (loading || !data) return <Spinner />;
  const c = data.consultant || {};
  const k = data.kpi || {};
  const p = data.palier || {};

  const copyPromo = async () => {
    try {
      await navigator.clipboard.writeText(c.codePromo || "");
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch { /* ignore */ }
  };

  // Progression palier
  const nextSeuil = p.prochain === "Confirmé" ? p.seuil_confirme : p.prochain === "Expert" ? p.seuil_expert : p.seuil_expert;
  const pct = nextSeuil ? Math.min(100, Math.round((p.nb_ventes_mois || 0) / nextSeuil * 100)) : 100;

  return (
    <div>
      {aRelancer > 0 && (
        <div onClick={() => onNavigate("leads")} data-testid="dash-leads-a-relancer" style={{ cursor: "pointer", background: C.redlt, borderLeft: `3px solid ${C.red}`, color: C.red, borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>
          ⏰ <strong>{aRelancer} lead{aRelancer > 1 ? "s" : ""} à relancer</strong> — sans action depuis 7 jours. Voir mes leads →
        </div>
      )}
      {/* Bandeau bienvenue */}
      <Card style={{ marginBottom: 20, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, color: "#fff", border: "none" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 500 }} data-testid="consultant-greeting">
              Bonjour {c.prenom || c.nom} 👋
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: "rgba(255,255,255,.75)" }}>
              {c.zone && <span>{c.zone} · </span>}
              <span style={{ fontFamily: "monospace" }}>{c.agrement}</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,.08)", padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(200,144,44,.25)" }}>
            <div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.55)", textTransform: "uppercase", letterSpacing: ".07em" }}>Mon code promo</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.gold, fontFamily: "monospace" }} data-testid="promo-code">{c.codePromo || "—"}</div>
            </div>
            <Btn small variant="gold" onClick={copyPromo} data-testid="copy-promo-btn">{copyOk ? "✓ Copié" : "Copier"}</Btn>
          </div>
        </div>
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <MonthPicker value={mois} onChange={setMois} />
      </div>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Kpi data-testid="kpi-rdv" label="RDV à venir" value={k.rdv_a_venir}
             sub={k.prochain_rdv ? `Prochain : ${k.prochain_rdv.date} · ${k.prochain_rdv.heure}` : "Aucun"} color={C.blue} />
        <Kpi data-testid="kpi-bilans" label="Bilans réalisés" value={k.bilans_realises} sub="ce mois" color={C.mid} />
        <Kpi data-testid="kpi-propositions" label="Propositions en cours"
             value={k.propositions_en_cours_nb}
             sub={`Total : ${fmtEur(k.propositions_en_cours_montant || 0)}`} color={C.gold} />
        <Kpi data-testid="kpi-encaisse" label="Encaissé" value={fmtEur(k.encaisse_mois || 0)} sub="ce mois" color={C.teal} />
        <Kpi data-testid="kpi-commission" label="Commission estimée"
             value={fmtEur(k.commission_totale || 0)}
             sub={`Direct ${fmtEur(k.commission_self||0)} · Équipe ${fmtEur(k.commission_equipe||0)}`} color={C.golddm} />
      </div>

      {/* Palier */}
      <Section title="Mon palier du mois" data-testid="section-palier">
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            {[
              { name: "Starter", range: `1 – ${p.seuil_confirme - 1} ventes`, taux: "20%", color: C.blue },
              { name: "Confirmé", range: `${p.seuil_confirme} – ${p.seuil_expert - 1} ventes`, taux: "25%", color: C.gold },
              { name: "Expert", range: `${p.seuil_expert}+ ventes`, taux: "30%", color: C.teal },
            ].map(t => (
              <div key={t.name} style={{
                padding: 12, borderRadius: 10,
                background: p.actuel === t.name ? t.color + "18" : C.gray2,
                border: `1.5px solid ${p.actuel === t.name ? t.color : C.border}`,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.color, textTransform: "uppercase", letterSpacing: ".07em" }}>{t.name} — {t.taux}</div>
                <div style={{ fontSize: 12, color: C.mid, marginTop: 4 }}>{t.range}</div>
                {p.actuel === t.name && <div style={{ fontSize: 10, fontWeight: 600, color: t.color, marginTop: 4 }}>◆ Palier actuel</div>}
              </div>
            ))}
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
              <span style={{ color: C.mid }}>Ventes du mois : <strong style={{ color: C.navy }}>{p.nb_ventes_mois}</strong> {nextSeuil ? `/ ${nextSeuil}` : ""}</span>
              {p.prochain ? (
                <span style={{ fontWeight: 600, color: C.gold }} data-testid="palier-progress-text">
                  Encore <strong>{p.encore_avant_prochain}</strong> vente{p.encore_avant_prochain > 1 ? "s" : ""} pour passer <strong>{p.prochain}</strong>
                </span>
              ) : (
                <span style={{ fontWeight: 600, color: C.teal }}>🏆 Palier maximum atteint</span>
              )}
            </div>
            <div style={{ height: 10, background: C.border, borderRadius: 5, overflow: "hidden" }}>
              <div data-testid="palier-progress-bar" style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.golddm})`, transition: "width .5s" }} />
            </div>
          </div>
        </Card>
      </Section>

      {/* Prochains RDV */}
      <MesObjectifs />

      <Section title="Prochains rendez-vous" right={
        <Btn small variant="gold" onClick={() => onNavigate("parcours")} data-testid="btn-new-parcours">
          + Nouveau parcours client
        </Btn>
      }>
        {data.prochains_rdv?.length ? (
          <Card style={{ padding: 0 }}>
            <table style={tableStyle}>
              <thead><tr>{["Date", "Client", "Parcours", ""].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {data.prochains_rdv.map(r => (
                  <tr key={r.id} style={{ borderBottom: `.5px solid ${C.border}` }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{fdate(r.date)}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{r.heure}</div>
                    </td>
                    <td style={tdStyle}>{r.client || "—"}</td>
                    <td style={tdStyle}><Badge text={r.type_parcours_label} /></td>
                    <td style={tdStyle}>
                      <Btn small variant="gold" onClick={() => onDemarrerBilan?.(r.id)} data-testid={`rdv-demarrer-${r.id}`}>▶ Démarrer le bilan</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : <Card><div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 12 }}>Aucun rendez-vous à venir</div></Card>}
      </Section>

      {/* Propositions en attente + À rappeler côte à côte */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Section title="Propositions en attente" data-testid="section-propositions">
          <Card style={{ padding: 0 }}>
            {data.propositions_en_attente?.length ? (
              <table style={tableStyle}>
                <thead><tr>{["Client", "Formule", "Montant", "Suivi", "Statut"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.propositions_en_attente.map(p2 => (
                    <tr key={p2.id} style={{ borderBottom: `.5px solid ${C.border}`, cursor: "pointer" }} onClick={() => onNavigate("parcours")}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{p2.client}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{p2.type_parcours_label}</div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, textTransform: "capitalize" }}>{p2.formule || "—"}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: C.teal }}>{fmtEur(p2.montant)}</td>
                      <td style={{ ...tdStyle, fontSize: 11, color: C.mid }}>
                        {p2.date_ouverture ? "✓ Ouverte" : "◯ Non ouverte"} · {p2.relances_envoyees} relance{p2.relances_envoyees > 1 ? "s" : ""}
                      </td>
                      <td style={tdStyle}><StatutBadge statut={p2.statut} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 20 }}>Aucune proposition en attente</div>}
          </Card>
        </Section>

        <Section title="À rappeler" data-testid="section-a-rappeler">
          <Card style={{ padding: 0 }}>
            {data.a_rappeler?.length ? data.a_rappeler.map(r => (
              <div key={r.id} style={{ padding: 12, borderBottom: `.5px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{r.client}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{r.type_parcours_label} · {r.telephone || r.email}</div>
                </div>
                {r.telephone && (
                  <a href={`https://wa.me/${r.telephone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 11, background: "#25D366", color: "#fff", padding: "5px 10px", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}
                     data-testid={`wa-btn-${r.id}`}>💬 WhatsApp</a>
                )}
              </div>
            )) : <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 20 }}>Personne à rappeler</div>}
          </Card>
        </Section>
      </div>

      {/* Notifs WhatsApp à envoyer */}
      {data.notifs_whatsapp?.length > 0 && (
        <Section title="Relances WhatsApp à envoyer" data-testid="section-notifs-wa">
          <Card style={{ background: "#F0FDF4", border: `1px solid #86EFAC`, padding: 0 }}>
            {data.notifs_whatsapp.map(n => (
              <div key={n.id} style={{ padding: 12, borderBottom: `.5px solid #BBF7D0`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{n.client_prenom} {n.client_nom}</div>
                  <div style={{ fontSize: 11, color: C.mid }}>Relance J+{n.j} · {n.client_telephone}</div>
                </div>
                {n.client_telephone && (
                  <a href={`https://wa.me/${n.client_telephone.replace(/\D/g,'')}?text=${encodeURIComponent(n.message_prerempli || "")}`}
                     target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 11, background: "#25D366", color: "#fff", padding: "5px 10px", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}>
                    💬 Envoyer
                  </a>
                )}
              </div>
            ))}
          </Card>
        </Section>
      )}

      {/* Brouillons */}
      {data.brouillons?.length > 0 && (
        <Section title="Parcours en cours (brouillons à reprendre)" data-testid="section-brouillons">
          <Card style={{ padding: 0 }}>
            {data.brouillons.map(b => (
              <div key={b.id} onClick={() => onNavigate("parcours")}
                   style={{ padding: 12, borderBottom: `.5px solid ${C.border}`, cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{b.client}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{b.type_parcours_label} · MàJ {(b.date_maj || "").slice(0,10)}</div>
                </div>
                <div style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>Reprendre →</div>
              </div>
            ))}
          </Card>
        </Section>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════
// Étape 17 — listes d'action (compléments vue d'ensemble)
const AdminActions = ({ onNavigate }) => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/indicateurs/leads").then(r => setA(r.data.actions)).catch(() => {}); }, []);
  if (!a) return null;
  const items = [["Leads dormants (30 j)", a.dormants, "leads"], ["Doublons à trancher", a.doublons, "leads"], ["Transferts à valider", a.transferts, "validations"], ["Commissions à valider", a.commissions_a_valider, "commissions_valider"]];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }} data-testid="admin-actions">
      {items.map(([l, n, page]) => (
        <div key={l} onClick={() => onNavigate(page)} data-testid={`admin-action-${page}-${l.length}`} style={{ cursor: "pointer", background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${n ? C.red : C.green}`, borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{l}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.navy }}>{n}</div>
        </div>
      ))}
    </div>
  );
};

export const AdminDashboardV2 = ({ onNavigate }) => {
  const [mois, setMois] = useState(currentMonth());
  const [ov, setOv] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [rows, setRows] = useState(null);
  const [rep, setRep] = useState(null);
  const [demo, setDemo] = useState(null);
  const [alertes, setAlertes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      dashboardApi.admin(mois), dashboardApi.funnel(mois), dashboardApi.consultantsActivity(mois),
      dashboardApi.repartition(mois), dashboardApi.demographie(), dashboardApi.alertes(),
    ]).then(([a, f, c, r, d, al]) => {
      if (!alive) return;
      setOv(a); setFunnel(f); setRows(c); setRep(r); setDemo(d); setAlertes(al);
    }).catch(console.error).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [mois]);

  if (loading || !ov) return <Spinner />;
  const k = ov.kpi;

  return (
    <div>
      <AdminActions onNavigate={onNavigate} />
      {/* Header + sélecteur */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: C.navy }}>Vue d'ensemble AUPRÉA</div>
          <div style={{ fontSize: 12, color: C.muted, textTransform: "capitalize" }}>{monthLabel(mois)}</div>
        </div>
        <MonthPicker value={mois} onChange={setMois} />
      </div>

      {/* KPI Principaux */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 22 }}>
        <Kpi data-testid="admin-kpi-rdv" label="RDV planifiés" value={k.rdv_planifies} color={C.blue} />
        <Kpi data-testid="admin-kpi-bilans" label="Bilans réalisés" value={k.bilans_realises} color={C.mid} />
        <Kpi data-testid="admin-kpi-transfo" label="Taux de transformation" value={`${k.taux_transformation}%`}
             sub={`${k.dossiers_payes} payés / ${k.bilans_realises} bilans`} color={C.purp} />
        <Kpi data-testid="admin-kpi-ca" label="CA encaissé" value={fmtEur(k.ca_total)}
             sub={`Carte ${fmtEur(k.ca_carte)} · 3x ${fmtEur(k.ca_3x)} · Virement ${fmtEur(k.ca_virement)}`} color={C.teal} />
        <Kpi data-testid="admin-kpi-abo" label="Abonnements carnet" value={k.abonnements_actifs_nb}
             sub={`${fmtEur(k.abonnements_mensuel_total)} / mois`} color={C.gold} />
        <Kpi data-testid="admin-kpi-virements" label="Virements à valider" value={k.virements_a_valider} color={C.amber} />
        <Kpi data-testid="admin-kpi-comm" label="Commissions dues" value={fmtEur(k.commissions_dues)}
             sub={`${k.pct_commissions_ca}% du CA`} color={C.golddm} />
      </div>

      {/* Entonnoir + Répartition CA */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16, marginBottom: 22 }}>
        <Section title="Entonnoir global" data-testid="section-funnel">
          <Card>
            {funnel?.global && (
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={[
                    { etape: "RDV",                 val: funnel.global.rdv,          fill: C.blue },
                    { etape: "Bilans réalisés",     val: funnel.global.bilans,       fill: C.mid },
                    { etape: "Propositions envoyées", val: funnel.global.propositions, fill: C.gold },
                    { etape: "Payées",              val: funnel.global.payees,       fill: C.teal },
                  ]} margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.border} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="etape" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="val" radius={[0, 6, 6, 0]}>
                      {[C.blue, C.mid, C.gold, C.teal].map((c, i) => <Cell key={i} fill={c} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </Section>

        <Section title="Répartition du CA par parcours" data-testid="section-repartition">
          <Card>
            {rep?.par_parcours?.length ? (
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={rep.par_parcours} dataKey="montant" nameKey="label" cx="50%" cy="50%" outerRadius={80} label={(e) => e.label}>
                      {rep.par_parcours.map((_, i) => <Cell key={i} fill={[C.navy, C.gold, C.teal, C.purp, C.amber][i % 5]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEur(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 40 }}>Aucune donnée sur la période</div>}
          </Card>
        </Section>
      </div>

      {/* Activité par consultant */}
      <Section title="Activité par consultant" data-testid="section-consultants">
        <Card style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead><tr>{["Consultant", "Zone", "Parrain", "RDV", "Bilans", "Propositions", "Ventes", "Taux", "Encaissé", "Palier", "Commission"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {(rows?.rows || []).map(row => (
                  <tr key={row.conseillerId} style={{ borderBottom: `.5px solid ${C.border}` }} data-testid={`consultant-row-${row.conseillerId}`}>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: (row.color || C.mid) + "22", color: row.color || C.mid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{row.initials}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>{row.nom}</div>
                          <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{row.conseillerId}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{row.zone || "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: C.mid }}>{row.parrain || "Direct AUPRÉA"}</td>
                    <td style={tdStyle}>{row.rdv_a_venir}</td>
                    <td style={tdStyle}>{row.bilans}</td>
                    <td style={tdStyle}>{row.propositions}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.ventes}</td>
                    <td style={{ ...tdStyle, color: C.purp, fontWeight: 600 }}>{row.taux_transformation}%</td>
                    <td style={{ ...tdStyle, color: C.teal, fontWeight: 600 }}>{fmtEur(row.encaisse)}</td>
                    <td style={tdStyle}><Badge text={row.palier} /></td>
                    <td style={{ ...tdStyle, color: C.golddm, fontWeight: 600 }}>{fmtEur(row.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Section>

      {/* Démographie */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
        <Section title="Répartition par tranche d'âge" data-testid="section-age">
          <Card>
            {demo?.tranches_age?.length ? (
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={demo.tranches_age.filter(t => t.tranche !== "Non renseigné" || t.clients > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="tranche" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="clients" name="Clients" fill={C.navy} radius={[6, 6, 0, 0]} />
                    <Bar dataKey="ventes" name="Ventes" fill={C.gold} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 40 }}>Aucun client</div>}
          </Card>
        </Section>

        <Section title="Répartition par territoire" data-testid="section-territoire">
          <Card style={{ padding: 0 }}>
            {demo?.territoires?.length ? (
              <table style={tableStyle}>
                <thead><tr>{["Territoire", "Clients", "Ventes", "Encaissé", "Âge moyen"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {demo.territoires.map(t => (
                    <tr key={t.territoire} style={{ borderBottom: `.5px solid ${C.border}` }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{t.territoire}</td>
                      <td style={tdStyle}>{t.clients}</td>
                      <td style={tdStyle}>{t.ventes}</td>
                      <td style={{ ...tdStyle, color: C.teal, fontWeight: 600 }}>{fmtEur(t.encaisse)}</td>
                      <td style={{ ...tdStyle, color: C.mid }}>{t.age_moyen ? `${t.age_moyen} ans` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 40 }}>Aucune donnée</div>}
          </Card>
        </Section>
      </div>

      {/* Alertes */}
      <Section title="Alertes" data-testid="section-alertes">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          <AlertBox color={C.amber} label={`Dossiers sans action > 7j`} count={alertes?.dossiers_inactifs_7j?.length || 0}
                    items={alertes?.dossiers_inactifs_7j?.slice(0, 3).map(x => `${x.client} · ${x.type_parcours_label}`) || []} />
          <AlertBox color={C.red} label="Propositions expirées" count={alertes?.propositions_expirees?.length || 0}
                    items={alertes?.propositions_expirees?.slice(0, 3).map(x => `${x.client} · ${x.type_parcours_label}`) || []} />
          <AlertBox color={C.red} label="Échecs de paiement" count={alertes?.echecs_paiement?.length || 0}
                    items={alertes?.echecs_paiement?.slice(0, 3).map(x => x.message) || []} />
          <AlertBox color={C.blue} label="Bilans reçus à attribuer" count={alertes?.bilans_a_attribuer?.length || 0}
                    items={alertes?.bilans_a_attribuer?.slice(0, 3).map(x => x.message.replace(/^Nouveau bilan reçu de prive\.auprea\.com : /, "").replace(/ — à attribuer$/, "")) || []} />
          <AlertBox color={C.purp} label="Consultants sans bilan > 30j" count={alertes?.consultants_inactifs_30j?.length || 0}
                    items={alertes?.consultants_inactifs_30j?.slice(0, 3).map(x => `${x.prenom} ${x.nom}`) || []} />
        </div>
      </Section>
    </div>
  );
};

const AlertBox = ({ color, label, count, items }) => (
  <Card style={{ borderLeft: `4px solid ${color}` }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
    </div>
    <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>
      {items.length ? items.map((it, i) => <div key={i}>• {it}</div>) : <div style={{ color: C.muted, fontStyle: "italic" }}>Rien à signaler</div>}
    </div>
  </Card>
);

// ═══════════════════════════════════════════════════════════════════════════
// TOUS LES DOSSIERS (Admin)
// ═══════════════════════════════════════════════════════════════════════════
export const DossiersAdmin = ({ initialStatut = "" }) => {
  const [filters, setFilters] = useState({
    conseillerId: "", statut: initialStatut, type_parcours: "", territoire: "", mois: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conseillers, setConseillers] = useState([]);

  useEffect(() => {
    dashboardApi.consultantsActivity(currentMonth()).then(d => setConseillers(d.rows || [])).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    dashboardApi.dossiers(filters).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [filters]);

  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const csvUrl = useMemo(() => {
    const params = { ...filters };
    const token = localStorage.getItem("auprea_token");
    return { url: dashboardApi.exportCsvUrl(params), token };
  }, [filters]);

  const downloadCsv = async () => {
    try {
      const res = await fetch(csvUrl.url, { headers: { Authorization: `Bearer ${csvUrl.token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "dossiers_auprea.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <div style={{ minWidth: 180 }}>
          <Select value={filters.conseillerId} onChange={(e) => setF("conseillerId", e.target.value)} data-testid="filter-conseiller">
            <option value="">Tous les consultants</option>
            {conseillers.map(c => <option key={c.conseillerId} value={c.conseillerId}>{c.nom}</option>)}
          </Select>
        </div>
        <div style={{ minWidth: 180 }}>
          <Select value={filters.statut} onChange={(e) => setF("statut", e.target.value)} data-testid="filter-statut">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
        </div>
        <div style={{ minWidth: 180 }}>
          <Select value={filters.type_parcours} onChange={(e) => setF("type_parcours", e.target.value)} data-testid="filter-parcours">
            <option value="">Tous les parcours</option>
            <option value="bilan_succession">Bilan Succession</option>
            <option value="bilan_dirigeants">Bilan Dirigeants</option>
            <option value="ouvrir_succession">Ouvrir une succession</option>
            <option value="foncier_clair">Foncier Clair</option>
          </Select>
        </div>
        <div style={{ minWidth: 160 }}>
          <Select value={filters.territoire} onChange={(e) => setF("territoire", e.target.value)} data-testid="filter-territoire">
            <option value="">Tous les territoires</option>
            <option value="Guadeloupe">Guadeloupe</option>
            <option value="Martinique">Martinique</option>
            <option value="Guyane">Guyane</option>
            <option value="La Réunion">La Réunion</option>
            <option value="Mayotte">Mayotte</option>
            <option value="Autres Outre-mer">Autres Outre-mer</option>
            <option value="Hexagone">Hexagone</option>
          </Select>
        </div>
        <div style={{ width: 160 }}>
          <Input type="month" value={filters.mois} onChange={(e) => setF("mois", e.target.value)} data-testid="filter-mois" />
        </div>
        <div style={{ flex: 1 }} />
        <Btn variant="gold" onClick={downloadCsv} data-testid="btn-export-csv">↓ Export CSV</Btn>
      </div>

      {loading ? <Spinner /> : (
        <Card style={{ padding: 0 }}>
          <div style={{ padding: "10px 16px", fontSize: 12, color: C.mid, borderBottom: `.5px solid ${C.border}` }}>
            {data?.total || 0} dossier{(data?.total || 0) > 1 ? "s" : ""} · Affichage des {Math.min(1000, data?.total || 0)} premiers
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle} data-testid="dossiers-table">
              <thead><tr>{["Réf.", "Client", "Âge", "CP · Territoire", "Parcours", "Consultant", "RDV", "Statut", "Suivi", "Montant", "Paiement"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {(data?.rows || []).map(row => (
                  <tr key={row.id} style={{ borderBottom: `.5px solid ${C.border}` }} data-testid={`dossier-row-${row.id}`}>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 11 }}>{row.id}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{row.client || "—"}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{row.email}</div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{row.age ? `${row.age} ans` : "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{row.code_postal || "—"}<div style={{ fontSize: 10, color: C.muted }}>{row.territoire}</div></td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{row.type_parcours_label}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{row.conseiller_nom}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{row.rdv ? `${row.rdv.date} ${row.rdv.heure}` : "—"}</td>
                    <td style={tdStyle}><StatutBadge statut={row.statut} /></td>
                    <td style={{ ...tdStyle, fontSize: 11, color: C.mid }}>
                      {row.suivi.date_publication && (
                        <>
                          {row.suivi.date_ouverture ? "✓" : "◯"} · {row.suivi.nb_relances} relance{row.suivi.nb_relances > 1 ? "s" : ""}
                        </>
                      )}
                      {!row.suivi.date_publication && "—"}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: C.teal }}>{row.montant > 0 ? fmtEur(row.montant) : "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{row.payment_mode || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data?.rows?.length) && <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Aucun dossier trouvé</div>}
          </div>
        </Card>
      )}
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2, whiteSpace: "nowrap" };
const tdStyle = { padding: "10px 14px", verticalAlign: "middle" };
